import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Switch,
  Button,
  message,
  Spin,
  Divider,
  Typography,
  Space,
  Alert,
  Descriptions,
  InputNumber
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { systemSettingsAPI } from '../../services/api';

const { Title, Text, Paragraph } = Typography;

const SystemSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  // 获取系统配置
  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      const response = await systemSettingsAPI.getAll();
      
      if (response.data.success) {
        const settingsMap = {};
        response.data.data.forEach(setting => {
          let value = setting.setting_value;
          
          // 根据类型转换值
          if (setting.setting_type === 'boolean') {
            value = value === true || value === 'true' || value === '1' || value === 1;
          } else if (setting.setting_type === 'number') {
            value = Number(value);
          }
          
          settingsMap[setting.setting_key] = {
            value,
            description: setting.description,
            type: setting.setting_type
          };
        });
        
        setSettings(settingsMap);
        
        // 设置表单初始值
        const formValues = {};
        Object.keys(settingsMap).forEach(key => {
          formValues[key] = settingsMap[key].value;
        });
        form.setFieldsValue(formValues);
      } else {
        message.error(response.data.message || '获取配置失败');
      }
    } catch (error) {
      console.error('获取配置错误:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 保存配置
  const handleSave = async (values) => {
    try {
      setSaving(true);

      const response = await systemSettingsAPI.batchUpdate({ settings: values });

      if (response.data.success) {
        message.success('配置保存成功');
        fetchSettings(); // 重新获取配置
      } else {
        message.error(response.data.message || '保存失败');
      }
    } catch (error) {
      console.error('保存配置错误:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <SettingOutlined style={{ marginRight: '8px' }} />
          系统设置
        </Title>
        <Text type="secondary">配置系统级参数，控制用户注册、审核等功能</Text>
      </div>

      <Spin spinning={loading}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{
              require_user_approval: true,
              allow_teacher_self_registration: true,
              allow_school_admin_self_registration: false,
              watch_class_membership_required: false,
              client_downloads_enabled: true,
              max_invitation_code_uses: 10,
              invitation_code_expiry_days: 30
            }}
          >
            {/* 用户审核设置 */}
            <Title level={4}>用户审核设置</Title>
            <Alert
              message="控制用户注册后是否需要管理员审核"
              description="关闭审核后，用户注册即可直接使用系统。但建议标注未经审核的用户以便后续管理。"
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />

            <Form.Item
              name="require_user_approval"
              label="是否需要审核用户注册"
              valuePropName="checked"
              extra={
                settings.require_user_approval?.value 
                  ? "已开启：教师和学校管理员注册后需要审核才能使用系统"
                  : "已关闭：用户注册后可直接使用系统（自动激活）"
              }
            >
              <Switch
                checkedChildren="需要审核"
                unCheckedChildren="自动激活"
              />
            </Form.Item>

            <Divider />

            {/* 注册权限设置 */}
            <Title level={4}>注册权限设置</Title>
            
            <Form.Item
              name="allow_teacher_self_registration"
              label="允许教师自主注册"
              valuePropName="checked"
              extra="关闭后，教师只能通过邀请码注册"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="allow_school_admin_self_registration"
              label="允许学校管理员自主注册"
              valuePropName="checked"
              extra="关闭后，学校管理员只能通过邀请码注册"
            >
              <Switch />
            </Form.Item>

            <Divider />

            {/* 功能开放设置 */}
            <Title level={4}>功能开放设置</Title>
            <Alert
              message="开关由服务器统一执行"
              description="视频看班会员限制会在服务端校验，不能通过绕过小程序页面使用；下载开关只控制新用户公开下载，不影响已安装客户端在线更新。"
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />

            <Form.Item
              name="watch_class_membership_required"
              label="视频看班需要会员"
              valuePropName="checked"
              extra="开启后，只有具有有效会员记录的教师才能启动摄像头或桌面看班；平台管理员不受限制"
            >
              <Switch checkedChildren="需要会员" unCheckedChildren="所有授权教师" />
            </Form.Item>

            <Form.Item
              name="client_downloads_enabled"
              label="开放桌面客户端和教师工作台下载"
              valuePropName="checked"
              extra="关闭后，官网和微信小程序不再向新用户提供下载入口；已安装客户端仍可正常使用和在线更新"
            >
              <Switch checkedChildren="开放下载" unCheckedChildren="关闭下载" />
            </Form.Item>

            <Divider />

            {/* 邀请码设置 */}
            <Title level={4}>邀请码设置</Title>
            
            <Form.Item
              name="max_invitation_code_uses"
              label="邀请码默认最大使用次数"
              rules={[
                { required: true, message: '请输入最大使用次数' },
                { type: 'number', min: 1, max: 1000, message: '请输入1-1000之间的数字' }
              ]}
              extra="生成邀请码时的默认使用次数限制"
            >
              <InputNumber 
                min={1} 
                max={1000} 
                style={{ width: '200px' }}
                placeholder="默认10次"
              />
            </Form.Item>

            <Form.Item
              name="invitation_code_expiry_days"
              label="邀请码默认有效期（天）"
              rules={[
                { required: true, message: '请输入有效期天数' },
                { type: 'number', min: 1, max: 365, message: '请输入1-365之间的数字' }
              ]}
              extra="生成邀请码时的默认有效期"
            >
              <InputNumber 
                min={1} 
                max={365} 
                style={{ width: '200px' }}
                placeholder="默认30天"
              />
            </Form.Item>

            <Divider />

            {/* 当前配置摘要 */}
            <Title level={4}>当前配置摘要</Title>
            <Card size="small" style={{ backgroundColor: '#f5f5f5', marginBottom: '24px' }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="用户注册审核">
                  {settings.require_user_approval?.value ? (
                    <Text type="warning">需要审核 ✓</Text>
                  ) : (
                    <Text type="success">自动激活 ✓</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="教师自主注册">
                  {settings.allow_teacher_self_registration?.value ? (
                    <Text type="success">允许 ✓</Text>
                  ) : (
                    <Text type="warning">禁止（仅邀请码）</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="学校管理员自主注册">
                  {settings.allow_school_admin_self_registration?.value ? (
                    <Text type="success">允许 ✓</Text>
                  ) : (
                    <Text type="warning">禁止（仅邀请码）</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="视频看班会员限制">
                  {settings.watch_class_membership_required?.value ? (
                    <Text type="warning">需要有效会员</Text>
                  ) : (
                    <Text type="success">所有具备班级权限的教师可用</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="客户端公开下载">
                  {settings.client_downloads_enabled?.value === false ? (
                    <Text type="warning">已关闭</Text>
                  ) : (
                    <Text type="success">已开放 ✓</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* 操作按钮 */}
            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                  size="large"
                >
                  保存配置
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchSettings}
                  disabled={saving}
                  size="large"
                >
                  重新加载
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 使用说明 */}
        <Card
          title="配置说明"
          style={{ marginTop: '24px' }}
          size="small"
        >
          <Paragraph>
            <ul>
              <li>
                <Text strong>审核开关：</Text>控制用户注册后是否需要管理员审核。关闭后用户注册即可直接使用，
                但系统会标注这些用户为"未经审核"状态，方便后续管理。
              </li>
              <li>
                <Text strong>注册权限：</Text>可以控制教师和学校管理员是否能自主注册。
                关闭后只能通过邀请码注册，提高系统安全性。
              </li>
              <li>
                <Text strong>邀请码设置：</Text>配置生成邀请码时的默认参数，管理员在生成邀请码时可以自定义修改。
              </li>
              <li>
                <Text strong>看班会员开关：</Text>只在用户主动开始看班时校验，不增加摄像头状态轮询，也不会让每次心跳查询数据库。
              </li>
              <li>
                <Text strong>下载开关：</Text>控制新安装入口，不中断已安装教室客户端的更新和日常使用。
              </li>
              <li>
                <Text strong>安全建议：</Text>
                <ul>
                  <li>生产环境建议开启审核功能</li>
                  <li>学校管理员注册建议禁用自主注册，只允许超级管理员邀请</li>
                  <li>定期检查未审核用户，及时处理</li>
                </ul>
              </li>
            </ul>
          </Paragraph>
        </Card>
      </Spin>
    </div>
  );
};

export default SystemSettings;

